import { useState, useMemo } from 'react'
import {
  Search,
  ChevronDown,
  ChevronRight,
  Star,
  Volume2,
  VolumeX,
  Pause,
  Trash2,
  ExternalLink,
  Building2,
  Clock,
  Mail,
  Sparkles,
  Lightbulb,
  RefreshCw,
  Tag,
  Percent,
  User,
  Loader2,
  Timer,
  Bomb,
  AlertTriangle,
  Lock,
  Crown
} from 'lucide-react'
import { Timestamp } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { Vendor, VendorStatus, VendorCategory } from '../../types/vendor'
import { useAuth } from '../../contexts/AuthContext'
import { useSubscription } from '../../contexts/SubscriptionContext'
import {
  updateVendorStatus,
  toggleVendorVip,
  deleteVendor,
  markVendorAsHuman,
  unmarkVendorAsHuman,
  setVendorAutoDelete,
  setBatchAutoDelete
} from '../../services/vendorService'
import NukeProgressModal from './NukeProgressModal'
import { ProBadge } from '../UI/ProBadge'

interface VendorsTabProps {
  vendors: Vendor[]
  onVendorUpdate: () => void
  loading: boolean
}

const CATEGORY_LABELS: Record<VendorCategory, string> = {
  shopping: 'Shopping',
  travel: 'Travel',
  finance: 'Finance',
  social: 'Social',
  news: 'News',
  tech: 'Tech',
  food: 'Food',
  entertainment: 'Entertainment',
  health: 'Health',
  education: 'Education',
  other: 'Other'
}

const CATEGORY_COLORS: Record<VendorCategory, string> = {
  shopping: 'bg-orange-100 text-orange-700',
  travel: 'bg-blue-100 text-blue-700',
  finance: 'bg-green-100 text-green-700',
  social: 'bg-purple-100 text-purple-700',
  news: 'bg-gray-100 text-gray-700',
  tech: 'bg-indigo-100 text-indigo-700',
  food: 'bg-red-100 text-red-700',
  entertainment: 'bg-pink-100 text-pink-700',
  health: 'bg-emerald-100 text-emerald-700',
  education: 'bg-amber-100 text-amber-700',
  other: 'bg-slate-100 text-slate-700'
}

// Check if vendor was nuked within the last 3 days
const wasRecentlyNuked = (vendor: Vendor): boolean => {
  if (!vendor.nukedAt) return false
  const nukedAt = vendor.nukedAt instanceof Timestamp ? vendor.nukedAt.toDate() : new Date(vendor.nukedAt)
  const threeDaysAgo = new Date()
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3)
  return nukedAt > threeDaysAgo
}

// Get days since nuke
const getDaysSinceNuke = (vendor: Vendor): number => {
  if (!vendor.nukedAt) return 0
  const nukedAt = vendor.nukedAt instanceof Timestamp ? vendor.nukedAt.toDate() : new Date(vendor.nukedAt)
  const now = new Date()
  const diffMs = now.getTime() - nukedAt.getTime()
  return Math.floor(diffMs / (1000 * 60 * 60 * 24))
}

// Check if vendor was created within the last 5 days
const isNewVendor = (vendor: Vendor): boolean => {
  if (!vendor.createdAt) return false
  const createdAt = vendor.createdAt instanceof Timestamp ? vendor.createdAt.toDate() : new Date(vendor.createdAt)
  const fiveDaysAgo = new Date()
  fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5)
  return createdAt > fiveDaysAgo
}

export default function VendorsTab({ vendors, onVendorUpdate, loading }: VendorsTabProps) {
  const { user } = useAuth()
  const { canAutoDelete, canNuke } = useSubscription()
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [insightLoading, setInsightLoading] = useState<string | null>(null)
  const [batchLoading, setBatchLoading] = useState<number | null>(null)
  const [nukeModal, setNukeModal] = useState<{ isOpen: boolean; vendorId: string; vendorName: string }>({
    isOpen: false,
    vendorId: '',
    vendorName: ''
  })

  // Filter vendors - show all except human-marked
  const filteredVendors = useMemo(() => {
    let filtered = vendors.filter(v => !v.isHuman)

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(v =>
        v.name.toLowerCase().includes(query) ||
        v.domain.toLowerCase().includes(query)
      )
    }

    // Sort by last email date (most recent first)
    filtered.sort((a, b) => {
      const dateA = a.lastEmailAt instanceof Date ? a.lastEmailAt :
        (a.lastEmailAt as Timestamp)?.toDate?.() || new Date(0)
      const dateB = b.lastEmailAt instanceof Date ? b.lastEmailAt :
        (b.lastEmailAt as Timestamp)?.toDate?.() || new Date(0)
      return dateB.getTime() - dateA.getTime()
    })

    return filtered
  }, [vendors, searchQuery])

  const stats = useMemo(() => ({
    total: vendors.filter(v => !v.isHuman).length,
    withAutoDelete: vendors.filter(v => !v.isHuman && v.autoDeleteAfterDays && v.autoDeleteAfterDays > 0).length
  }), [vendors])

  const handleBatchAutoDelete = async (days: number) => {
    if (!user) return
    const confirmMsg = days === 0
      ? 'Remove auto-delete policy from ALL vendors?'
      : `Set ALL vendors to auto-delete emails after ${days} days?\n\nThis will apply to ${stats.total} vendors.`
    if (!confirm(confirmMsg)) return

    setBatchLoading(days)
    try {
      const count = await setBatchAutoDelete(user.uid, days)
      alert(`Updated ${count} vendors to ${days === 0 ? 'never auto-delete' : `auto-delete after ${days} days`}`)
      onVendorUpdate()
    } catch (error) {
      console.error('Failed to batch update auto-delete:', error)
      alert('Failed to update vendors. Please try again.')
    } finally {
      setBatchLoading(null)
    }
  }

  const handleStatusChange = async (vendorId: string, status: VendorStatus, pauseDays?: number) => {
    if (!user) return
    setActionLoading(vendorId)
    try {
      await updateVendorStatus(user.uid, vendorId, status, pauseDays)
      onVendorUpdate()
    } catch (error) {
      console.error('Failed to update vendor status:', error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleToggleVip = async (vendorId: string, currentVip: boolean) => {
    if (!user) return
    setActionLoading(vendorId)
    try {
      await toggleVendorVip(user.uid, vendorId, !currentVip)
      onVendorUpdate()
    } catch (error) {
      console.error('Failed to toggle VIP:', error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleDelete = async (vendorId: string) => {
    if (!user) return
    if (!confirm('Remove this vendor from your list? This won\'t unsubscribe you from their emails.')) return
    setActionLoading(vendorId)
    try {
      await deleteVendor(user.uid, vendorId)
      onVendorUpdate()
    } catch (error) {
      console.error('Failed to delete vendor:', error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleToggleHuman = async (vendorId: string, currentIsHuman: boolean) => {
    if (!user) return
    setActionLoading(vendorId)
    try {
      if (currentIsHuman) {
        await unmarkVendorAsHuman(user.uid, vendorId)
      } else {
        await markVendorAsHuman(user.uid, vendorId)
      }
      onVendorUpdate()
    } catch (error) {
      console.error('Failed to toggle human status:', error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleSetAutoDelete = async (vendorId: string, days: number) => {
    if (!user) return
    setActionLoading(vendorId)
    try {
      await setVendorAutoDelete(user.uid, vendorId, days)
      onVendorUpdate()
    } catch (error) {
      console.error('Failed to set auto-delete:', error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleNukeVendor = (vendorId: string, vendorName: string) => {
    if (!user) return
    setNukeModal({ isOpen: true, vendorId, vendorName })
  }

  const executeNuke = async () => {
    const functions = getFunctions()
    const nukeVendorEmails = httpsCallable(functions, 'nukeVendorEmails')
    const result = await nukeVendorEmails({ vendorId: nukeModal.vendorId }) as {
      data: {
        deleted: number
        details?: {
          vendorName: string
          vendorDomain: string
          totalScanned: number
          matchedByVendorId: number
          matchedByDomain: number
          matchedBySender: number
          deletedFromImap: number
          deletedFromFirestore: number
          promotionsDeleted: number
          updatesDeleted: number
          deepSearchFound?: number
          deepSearchDeleted?: number
          deepSearchCycles?: number
          deepSearchSamples?: string[]
          totalImapDeleted?: number
        }
      }
    }
    return result.data
  }

  const handleGenerateInsight = async (vendorId: string) => {
    if (!user) return
    setInsightLoading(vendorId)
    try {
      const functions = getFunctions()
      const getVendorInsight = httpsCallable(functions, 'getVendorInsight')
      await getVendorInsight({ vendorId })
      onVendorUpdate()
    } catch (error) {
      console.error('Failed to generate insight:', error)
    } finally {
      setInsightLoading(null)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-50 overflow-hidden">
      {/* NUKE Progress Modal */}
      <NukeProgressModal
        isOpen={nukeModal.isOpen}
        vendorId={nukeModal.vendorId}
        vendorName={nukeModal.vendorName}
        onClose={() => setNukeModal({ isOpen: false, vendorId: '', vendorName: '' })}
        onConfirm={executeNuke}
        onComplete={onVendorUpdate}
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              {stats.total} Companies
            </h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              {stats.withAutoDelete > 0 && (
                <span className="text-amber-600">
                  <Timer size={12} className="inline mr-1" />
                  {stats.withAutoDelete} with auto-delete
                </span>
              )}
              {stats.withAutoDelete === 0 && 'Manage auto-delete policies for vendor emails'}
            </p>
          </div>
        </div>

        {/* Search and batch auto-delete - Stack on mobile */}
        <div className="flex flex-col lg:flex-row gap-3 mt-4">
          {/* Search bar - full width on mobile */}
          <div className="relative w-full lg:flex-1 lg:max-w-sm">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search companies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 sm:py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Batch auto-delete buttons - scrollable on mobile */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-500 shrink-0">Auto-delete all:</span>
              {!canAutoDelete && <ProBadge />}
            </div>
            {canAutoDelete ? (
              <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 -mx-3 px-3 sm:mx-0 sm:px-0">
                {[
                  { days: 7, label: '7d' },
                  { days: 14, label: '14d' },
                  { days: 30, label: '30d' },
                  { days: 0, label: 'Never' }
                ].map(({ days, label }) => (
                  <button
                    key={days}
                    onClick={() => handleBatchAutoDelete(days)}
                    disabled={batchLoading !== null}
                    className={`min-h-[44px] min-w-[44px] px-3 sm:px-3 py-2 text-sm rounded-lg transition-colors flex items-center justify-center gap-1.5 shrink-0 ${
                      days === 0
                        ? 'bg-white border text-gray-600 hover:bg-gray-50 active:bg-gray-100'
                        : 'bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 active:bg-amber-200'
                    } disabled:opacity-50`}
                  >
                    {batchLoading === days ? (
                      <Loader2 size={14} className="animate-spin" />
                    ) : days > 0 ? (
                      <Timer size={14} />
                    ) : null}
                    {label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Lock size={14} />
                <span>Upgrade to Pro</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Vendor Tiles */}
      <div className="flex-1 overflow-y-auto p-2 sm:p-4">
        {filteredVendors.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500 px-4">
            <Building2 className="w-10 h-10 sm:w-12 sm:h-12 mb-4 opacity-50" />
            <p className="text-base sm:text-lg font-medium text-center">No companies found</p>
            <p className="text-xs sm:text-sm mt-1 text-center">
              {searchQuery ? 'Try a different search term' : 'Companies will appear as you receive emails'}
            </p>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {filteredVendors.map(vendor => (
              <VendorTile
                key={vendor.id}
                vendor={vendor}
                isExpanded={expandedVendor === vendor.id}
                onToggle={() => setExpandedVendor(expandedVendor === vendor.id ? null : vendor.id!)}
                onGenerateInsight={() => handleGenerateInsight(vendor.id!)}
                isGeneratingInsight={insightLoading === vendor.id}
                isActionLoading={actionLoading === vendor.id}
                onStatusChange={handleStatusChange}
                onToggleVip={handleToggleVip}
                onToggleHuman={handleToggleHuman}
                onSetAutoDelete={handleSetAutoDelete}
                onNuke={handleNukeVendor}
                onDelete={handleDelete}
                canAutoDelete={canAutoDelete}
                canNuke={canNuke}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface VendorTileProps {
  vendor: Vendor
  isExpanded: boolean
  onToggle: () => void
  onGenerateInsight: () => void
  isGeneratingInsight: boolean
  isActionLoading: boolean
  onStatusChange: (vendorId: string, status: VendorStatus, pauseDays?: number) => void
  onToggleVip: (vendorId: string, currentVip: boolean) => void
  onToggleHuman: (vendorId: string, currentIsHuman: boolean) => void
  onSetAutoDelete: (vendorId: string, days: number) => void
  onNuke: (vendorId: string, vendorName: string) => void
  onDelete: (vendorId: string) => void
  // Subscription
  canAutoDelete: boolean
  canNuke: boolean
}

/**
 * Vendor logo component with graceful fallback
 */
function VendorLogo({ vendor, hasPromos }: { vendor: Vendor; hasPromos: boolean }) {
  const [imgError, setImgError] = useState(false)

  if (vendor.logoUrl && !imgError) {
    return (
      <img
        src={vendor.logoUrl}
        alt={vendor.name}
        className="w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl object-contain bg-white p-0.5 sm:p-1"
        onError={() => setImgError(true)}
      />
    )
  }

  return (
    <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center text-white font-bold text-base sm:text-xl ${
      hasPromos ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : 'bg-gray-400'
    }`}>
      {vendor.name.charAt(0).toUpperCase()}
    </div>
  )
}

function VendorTile({
  vendor,
  isExpanded,
  onToggle,
  onGenerateInsight,
  isGeneratingInsight,
  isActionLoading,
  onStatusChange,
  onToggleVip,
  onToggleHuman,
  onSetAutoDelete,
  onNuke,
  onDelete,
  canAutoDelete,
  canNuke
}: VendorTileProps) {
  const formatDate = (date: Date | Timestamp | null | undefined) => {
    if (!date) return 'Never'
    const d = date instanceof Date ? date : (date as Timestamp).toDate?.() || new Date(0)
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (hours < 1) return 'Just now'
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    if (days < 30) return `${Math.floor(days / 7)}w ago`
    return `${Math.floor(days / 30)}mo ago`
  }

  const promoCount = vendor.promotionCount || 0
  const updateCount = vendor.updateCount || 0
  const hasPromos = promoCount > 0

  return (
    <div className={`bg-white rounded-lg sm:rounded-xl border-2 transition-all ${
      vendor.status !== 'active' ? 'opacity-60' : ''
    } ${
      isExpanded ? 'border-indigo-300 shadow-lg' : 'border-gray-200 hover:border-gray-300 active:border-gray-400'
    }`}>
      {/* Tile Header - touch-friendly with min height */}
      <div className="p-3 sm:p-4 cursor-pointer min-h-[72px]" onClick={onToggle}>
        <div className="flex items-start gap-2 sm:gap-4">
          {/* Vendor Logo */}
          <div className="flex-shrink-0">
            <VendorLogo vendor={vendor} hasPromos={hasPromos} />
          </div>

          {/* Vendor Info */}
          <div className="flex-1 min-w-0">
            {/* Name and status icons row */}
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-900 text-base sm:text-lg truncate max-w-[150px] sm:max-w-none">{vendor.name}</h3>
              {vendor.isHuman && <User size={14} className="text-blue-500 flex-shrink-0 sm:w-4 sm:h-4" />}
              {vendor.isVip && <Star size={14} className="text-yellow-500 fill-yellow-500 flex-shrink-0 sm:w-4 sm:h-4" />}
              {vendor.status === 'muted' && <VolumeX size={14} className="text-gray-400 flex-shrink-0 sm:w-4 sm:h-4" />}
              {vendor.status === 'paused' && <Pause size={14} className="text-orange-500 flex-shrink-0 sm:w-4 sm:h-4" />}
            </div>

            {/* Badges row - wrap on mobile */}
            <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap mt-1">
              {vendor.isHuman ? (
                <span className="px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs rounded-full bg-blue-100 text-blue-700">
                  Human
                </span>
              ) : (
                <span className={`px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs rounded-full ${CATEGORY_COLORS[vendor.category]}`}>
                  {CATEGORY_LABELS[vendor.category]}
                </span>
              )}
              {/* Recently nuked indicator (shows for 3 days) */}
              {wasRecentlyNuked(vendor) && (
                <span
                  className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 bg-red-100 text-red-700 text-[10px] sm:text-xs rounded-full"
                  title={`Nuked ${getDaysSinceNuke(vendor) === 0 ? 'today' : `${getDaysSinceNuke(vendor)} day${getDaysSinceNuke(vendor) > 1 ? 's' : ''} ago`}${vendor.lastNukeDeleted ? ` - ${vendor.lastNukeDeleted} emails deleted` : ''}`}
                >
                  <Bomb size={10} className="sm:w-3 sm:h-3" />
                  Nuked
                </span>
              )}
              {/* Auto-delete indicator */}
              {vendor.autoDeleteAfterDays && vendor.autoDeleteAfterDays > 0 && (
                <span className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 bg-amber-100 text-amber-700 text-[10px] sm:text-xs rounded-full" title={`Auto-deletes after ${vendor.autoDeleteAfterDays} days`}>
                  <Timer size={10} className="animate-pulse sm:w-3 sm:h-3" />
                  {vendor.autoDeleteAfterDays}d
                </span>
              )}
              {/* New vendor indicator (shows for 5 days) */}
              {isNewVendor(vendor) && (
                <span className="flex items-center gap-0.5 sm:gap-1 px-1.5 sm:px-2 py-0.5 bg-green-100 text-green-700 text-[10px] sm:text-xs rounded-full" title="New vendor - added within last 5 days">
                  <Sparkles size={10} className="sm:w-3 sm:h-3" />
                  New
                </span>
              )}
            </div>

            {/* Company description - hidden on very small screens, 1 line on mobile */}
            <p className="hidden sm:block text-xs sm:text-sm text-gray-600 mt-1 line-clamp-1 sm:line-clamp-2">
              {vendor.companyInfo?.description || `Emails from ${vendor.domain}`}
            </p>

            {/* Meta info - compact on mobile */}
            <div className="flex items-center gap-2 sm:gap-4 mt-1.5 sm:mt-2 text-[10px] sm:text-xs text-gray-500 flex-wrap">
              <span className="flex items-center gap-0.5 sm:gap-1">
                <Mail size={10} className="sm:w-3 sm:h-3" />
                {vendor.totalEmailsReceived}
              </span>
              <span className="flex items-center gap-0.5 sm:gap-1">
                <Clock size={10} className="sm:w-3 sm:h-3" />
                {formatDate(vendor.lastEmailAt)}
              </span>
              {promoCount > 0 && (
                <span className="flex items-center gap-0.5 sm:gap-1 text-green-600 font-medium">
                  <Tag size={10} className="sm:w-3 sm:h-3" />
                  {promoCount}
                </span>
              )}
              {updateCount > 0 && (
                <span className="flex items-center gap-0.5 sm:gap-1 text-blue-600 font-medium">
                  <Percent size={10} className="sm:w-3 sm:h-3" />
                  {updateCount}
                </span>
              )}
            </div>
          </div>

          {/* Expand Arrow - larger touch target */}
          <div className="flex-shrink-0 text-gray-400 p-1 -m-1">
            {isExpanded ? <ChevronDown size={20} className="sm:w-6 sm:h-6" /> : <ChevronRight size={20} className="sm:w-6 sm:h-6" />}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-100">
          {/* Company Info */}
          {vendor.companyInfo && (
            <div className="p-3 sm:p-4 bg-gray-50 border-b border-gray-100">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-600">
                <div className="flex items-center gap-1.5">
                  <Building2 size={14} />
                  <span className="font-medium">{vendor.companyInfo.industry}</span>
                </div>
                {vendor.domain && (
                  <>
                    <span className="hidden sm:inline text-gray-300">|</span>
                    <a
                      href={`https://${vendor.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 hover:text-indigo-800 active:text-indigo-900 flex items-center gap-1 min-h-[44px] sm:min-h-0 py-2 sm:py-0"
                      onClick={e => e.stopPropagation()}
                    >
                      {vendor.domain}
                      <ExternalLink size={12} />
                    </a>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Actions - Grid layout on mobile for touch targets */}
          <div className="p-3 sm:p-4 border-b border-gray-100">
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); onToggleHuman(vendor.id!, vendor.isHuman || false); }}
                disabled={isActionLoading}
                className={`min-h-[44px] flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 sm:py-1.5 text-xs sm:text-sm rounded-lg transition-colors ${
                  vendor.isHuman
                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200 active:bg-blue-300'
                    : 'bg-white border text-gray-600 hover:bg-gray-100 active:bg-gray-200'
                }`}
              >
                {isActionLoading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <User size={14} />
                )}
                <span className="truncate">{vendor.isHuman ? 'As Vendor' : 'As Human'}</span>
              </button>

              <button
                onClick={(e) => { e.stopPropagation(); onToggleVip(vendor.id!, vendor.isVip); }}
                disabled={isActionLoading}
                className={`min-h-[44px] flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 sm:py-1.5 text-xs sm:text-sm rounded-lg transition-colors ${
                  vendor.isVip
                    ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 active:bg-yellow-300'
                    : 'bg-white border text-gray-600 hover:bg-gray-100 active:bg-gray-200'
                }`}
              >
                <Star size={14} className={vendor.isVip ? 'fill-yellow-500' : ''} />
                <span className="truncate">{vendor.isVip ? 'Remove VIP' : 'Mark VIP'}</span>
              </button>

              {vendor.status === 'active' ? (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); onStatusChange(vendor.id!, 'paused', 30); }}
                    disabled={isActionLoading}
                    className="min-h-[44px] flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 sm:py-1.5 text-xs sm:text-sm rounded-lg bg-white border text-gray-600 hover:bg-gray-100 active:bg-gray-200"
                  >
                    <Pause size={14} />
                    Pause 30d
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onStatusChange(vendor.id!, 'muted'); }}
                    disabled={isActionLoading}
                    className="min-h-[44px] flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 sm:py-1.5 text-xs sm:text-sm rounded-lg bg-white border text-gray-600 hover:bg-gray-100 active:bg-gray-200"
                  >
                    <VolumeX size={14} />
                    Mute
                  </button>
                </>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); onStatusChange(vendor.id!, 'active'); }}
                  disabled={isActionLoading}
                  className="min-h-[44px] flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 sm:py-1.5 text-xs sm:text-sm rounded-lg bg-white border text-gray-600 hover:bg-gray-100 active:bg-gray-200"
                >
                  <Volume2 size={14} />
                  Unmute
                </button>
              )}

              {vendor.unsubscribeLink && (
                <a
                  href={vendor.unsubscribeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="min-h-[44px] flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 sm:py-1.5 text-xs sm:text-sm rounded-lg bg-red-50 text-red-600 hover:bg-red-100 active:bg-red-200"
                >
                  <ExternalLink size={14} />
                  Unsubscribe
                </a>
              )}

              <button
                onClick={(e) => { e.stopPropagation(); onDelete(vendor.id!); }}
                disabled={isActionLoading}
                className="min-h-[44px] flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 sm:py-1.5 text-xs sm:text-sm rounded-lg bg-white border text-gray-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 active:bg-red-100"
              >
                <Trash2 size={14} />
                Remove
              </button>
            </div>
          </div>

          {/* Inbox Management */}
          <div className="p-3 sm:p-4 border-b border-gray-100 bg-gray-50">
            <p className="text-xs font-medium text-gray-500 mb-3 flex items-center gap-1.5">
              <Timer size={12} />
              Inbox Management
              {(!canAutoDelete || !canNuke) && (
                <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-medium rounded-full">
                  <Crown size={10} />
                  PRO
                </span>
              )}
            </p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
              {/* Auto-delete toggle */}
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <span className="text-xs sm:text-sm text-gray-600 shrink-0">Auto-delete after:</span>
                {canAutoDelete ? (
                  <select
                    value={vendor.autoDeleteAfterDays || 0}
                    onChange={(e) => {
                      e.stopPropagation()
                      onSetAutoDelete(vendor.id!, parseInt(e.target.value))
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isActionLoading}
                    className="flex-1 sm:flex-none min-h-[44px] text-sm border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value={0}>Never</option>
                    <option value={7}>7 days</option>
                    <option value={14}>14 days</option>
                    <option value={30}>30 days</option>
                    <option value={60}>60 days</option>
                    <option value={90}>90 days</option>
                  </select>
                ) : (
                  <span className="flex items-center gap-1 text-sm text-gray-400">
                    <Lock size={14} />
                    Pro only
                  </span>
                )}
              </div>

              {/* NUKE button */}
              {canNuke ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onNuke(vendor.id!, vendor.name)
                  }}
                  disabled={isActionLoading}
                  className="w-full sm:w-auto min-h-[44px] flex items-center justify-center gap-1.5 px-4 py-2 sm:py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 active:bg-red-800 disabled:opacity-50 transition-colors"
                >
                  {isActionLoading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Bomb size={14} />
                  )}
                  Delete All Emails
                </button>
              ) : (
                <button
                  disabled
                  className="w-full sm:w-auto min-h-[44px] flex items-center justify-center gap-1.5 px-4 py-2 sm:py-1.5 text-sm rounded-lg bg-gray-200 text-gray-400 cursor-not-allowed"
                >
                  <Lock size={14} />
                  NUKE (Pro)
                </button>
              )}
            </div>
            {canAutoDelete && vendor.autoDeleteAfterDays && vendor.autoDeleteAfterDays > 0 && (
              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                <AlertTriangle size={12} />
                <span className="line-clamp-2">Emails from {vendor.name} will be auto-deleted after {vendor.autoDeleteAfterDays} days</span>
              </p>
            )}
          </div>

          {/* Sender emails */}
          {vendor.senderEmails.length > 0 && (
            <div className="px-3 sm:px-4 py-3 border-b border-gray-100">
              <p className="text-xs font-medium text-gray-500 mb-2">Email addresses:</p>
              <div className="flex flex-wrap gap-1">
                {vendor.senderEmails.slice(0, 3).map((email, i) => (
                  <span key={i} className="text-[10px] sm:text-xs bg-gray-100 text-gray-600 px-1.5 sm:px-2 py-0.5 rounded truncate max-w-[140px] sm:max-w-none">
                    {email}
                  </span>
                ))}
                {vendor.senderEmails.length > 3 && (
                  <span className="text-[10px] sm:text-xs text-gray-400">
                    +{vendor.senderEmails.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* AI Insight */}
          <div className="p-3 sm:p-4">
            {vendor.aiInsight ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-indigo-600">
                    <Sparkles size={12} />
                    AI Insight
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); onGenerateInsight(); }}
                    disabled={isGeneratingInsight}
                    className="min-h-[44px] min-w-[44px] flex items-center justify-center gap-1 text-xs text-gray-400 hover:text-gray-600 active:text-gray-800 -m-2 p-2"
                  >
                    <RefreshCw size={12} className={isGeneratingInsight ? 'animate-spin' : ''} />
                    <span className="hidden sm:inline">Refresh</span>
                  </button>
                </div>
                <p className="text-xs sm:text-sm text-gray-700">{vendor.aiInsight.summary}</p>
                {vendor.aiInsight.emailTypes.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {vendor.aiInsight.emailTypes.map((type, i) => (
                      <span key={i} className="text-[10px] sm:text-xs bg-indigo-50 text-indigo-600 px-1.5 sm:px-2 py-0.5 rounded">
                        {type}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <Lightbulb size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-[10px] sm:text-xs text-amber-800">{vendor.aiInsight.recommendation}</p>
                </div>
              </div>
            ) : (
              <button
                onClick={(e) => { e.stopPropagation(); onGenerateInsight(); }}
                disabled={isGeneratingInsight}
                className="w-full min-h-[44px] flex items-center justify-center gap-2 px-3 py-3 sm:py-2 text-sm rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 active:bg-indigo-200 transition-colors disabled:opacity-50"
              >
                {isGeneratingInsight ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" />
                    <span className="text-xs sm:text-sm">Generating insight...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={14} />
                    <span className="text-xs sm:text-sm">Generate AI Insight</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
